import { useMemo, useState } from 'react'
import { Download, ExternalLink, Upload } from 'lucide-react'
import {
  Button,
  EmptyState,
  ExportDrawer,
  FullscreenButton,
} from '@/components/ui'
import { odooVendorSupplierinfoUrl } from '@/lib/utils/odoo-url'
import { useVendorPricelistItems } from '../api/queries'
import type { VendorItem, VendorRow } from '../api/types'
import { COLUMNS as VENDOR_EXPORT_COLUMNS, slugForFilename } from '../utils/csv'
import { VendorPriceSheetImportDrawer } from './VendorPriceSheetImportDrawer'
import { VendorPricelistEditor } from './VendorPricelistEditor'

export interface VendorPricelistDetailProps {
  vendor: VendorRow
  fullscreen?: boolean
  onToggleFullscreen?: () => void
}

/**
 * Mirrors `customer-pricelists/components/PricelistDetail.tsx` structure:
 * header card on top (vendor name · currency · location · items count ·
 * Open in Odoo · Refresh · Fullscreen · Export), EditableGrid below for
 * inline edits.
 *
 * Edits commit immediately to Odoo (same convention as Customer
 * Pricelists). Sample products are filtered server-side so they never
 * appear here.
 */
export function VendorPricelistDetail({
  vendor,
  fullscreen,
  onToggleFullscreen,
}: VendorPricelistDetailProps) {
  const { data, isLoading, isFetching, isError, refetch } = useVendorPricelistItems(vendor.id)
  const items = useMemo<VendorItem[]>(() => data?.items ?? [], [data])
  const [exportOpen, setExportOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)

  // Primary currency = whatever shows up most among this vendor's rows;
  // fall back to the first row's currency. Surfaces in the header card.
  const primaryCurrency = useMemo<string | null>(() => {
    if (items.length === 0) return null
    const counts = new Map<string, number>()
    for (const it of items) {
      if (!it.currency_name) continue
      counts.set(it.currency_name, (counts.get(it.currency_name) ?? 0) + 1)
    }
    let top: string | null = null
    let topN = 0
    for (const [k, n] of counts) {
      if (n > topN) {
        top = k
        topN = n
      }
    }
    return top
  }, [items])

  return (
    <div className="flex flex-col gap-4">
      <HeaderCard
        vendor={vendor}
        currency={primaryCurrency}
        fetching={isFetching}
        onRefresh={() => refetch()}
        onExport={() => setExportOpen(true)}
        onImport={() => setImportOpen(true)}
        exportDisabled={items.length === 0}
        fullscreen={fullscreen}
        onToggleFullscreen={onToggleFullscreen}
      />
      {isLoading ? (
        <p className="text-sm text-tertiary">Loading items…</p>
      ) : isError ? (
        <EmptyState
          variant="error"
          title="Couldn't load items"
          description="The /api/vendor-pricelists/{id}/items endpoint returned an error. Try Refresh."
        />
      ) : (
        <VendorPricelistEditor
          vendor={vendor}
          items={items}
          refetch={() => void refetch()}
          fetching={isFetching}
        />
      )}

      <ExportDrawer
        open={exportOpen}
        onOpenChange={setExportOpen}
        rows={items}
        columns={VENDOR_EXPORT_COLUMNS}
        moduleId="vendor-pricelists"
        filenameBase={`vendor-${slugForFilename(vendor.name)}`}
      />

      <VendorPriceSheetImportDrawer
        open={importOpen}
        onOpenChange={setImportOpen}
        vendorId={vendor.id}
        vendorName={vendor.name}
      />
    </div>
  )
}

function HeaderCard({
  vendor,
  currency,
  fetching,
  onRefresh,
  onExport,
  onImport,
  exportDisabled,
  fullscreen,
  onToggleFullscreen,
}: {
  vendor: VendorRow
  currency: string | null
  fetching: boolean
  onRefresh: () => void
  onExport: () => void
  onImport: () => void
  exportDisabled: boolean
  fullscreen?: boolean
  onToggleFullscreen?: () => void
}) {
  return (
    <section
      data-test="vendor-pricelist-header-band"
      className="flex items-center justify-between gap-3 rounded-sm border border-border-subtle bg-surface-sunk px-3 py-2 text-sm"
    >
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-role-data-primary truncate">{vendor.name}</span>
        <span className="text-tertiary">·</span>
        <span className="text-secondary">{currency ?? '—'}</span>
        {vendor.country_name ? (
          <>
            <span className="text-tertiary">·</span>
            <span className="text-secondary truncate">{vendor.country_name}</span>
          </>
        ) : null}
        {vendor.city ? (
          <>
            <span className="text-tertiary">·</span>
            <span className="text-secondary truncate">{vendor.city}</span>
          </>
        ) : null}
        <span className="text-tertiary">·</span>
        <span className="text-secondary">
          {vendor.item_count.toLocaleString()} items
        </span>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">
        <a
          href={odooVendorSupplierinfoUrl(vendor.id)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-link hover:underline"
        >
          Open in Odoo <ExternalLink size={10} />
        </a>
        <button
          type="button"
          onClick={onRefresh}
          disabled={fetching}
          className="inline-flex cursor-pointer items-center rounded-xs border border-border-default bg-surface px-2 py-0.5 text-2xs uppercase tracking-widest text-secondary hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          {fetching ? 'Refreshing…' : 'Refresh'}
        </button>
        {onToggleFullscreen ? (
          <FullscreenButton
            fullscreen={Boolean(fullscreen)}
            onToggle={onToggleFullscreen}
          />
        ) : null}
        <Button
          variant="secondary"
          size="sm"
          onClick={onImport}
          title="Upload or paste a vendor price sheet for review"
        >
          <Upload size={12} /> Import price sheet…
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onExport}
          disabled={exportDisabled}
          title="Pick columns or apply a saved template before download"
        >
          <Download size={12} /> Export…
        </Button>
      </div>
    </section>
  )
}
